import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Badge,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
// import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  fetchSourcingOverview,
  fetchSourcingPerformance,
  fetchTeamLeaderboard,
  clearSourcingError,
  setSourcingFilters,
} from "../../../store/procurementDashboardSlice";
import OverviewDashboard from "./OverviewDashboard";
import AnalyticsDashboard from "./AnalyticsDashboard";
import PerformanceDashboard from "./PerformanceDashboard";
import ReportsDashboard from "./ReportsDashboard";
import TalkWithDataModal from "../../ContractDashboard/TalkWithDataModal";
import FilterModal, { FilterValues } from "../FilterModal";
import overviewIcon from "../../../assets/images/overviewImg.svg";
import analyticsIcon from "../../../assets/images/analyticsIcon.svg";
import performanceIcon from "../../../assets/images/performanceImg.svg";
import supplierIcon from "../../../assets/images/supplierIcon1.svg";
import styles from "./sourcingSection.module.scss";

const parseFormattedNumber = (value: string | number): number => {
  if (typeof value === "number") return value;
  const str = String(value).toLowerCase().trim();
  const multipliers: Record<string, number> = {
    k: 1000,
    m: 1000000,
    b: 1000000000,
  };
  const match = str.match(/^([\d.]+)([kmb]?)$/);
  if (!match) return 0;
  const [, num, suffix] = match;
  return parseFloat(num) * (multipliers[suffix] || 1);
};

// Utility function to transform monthly data with year handling
const transformMonthlyPRData = (
  data: Array<{ month_name: string; month_num: number; year?: number; closed_prs: number; open_prs: number }>
): Array<{ month: string; closed: number; open: number }> => {
  if (!data || data.length === 0) return [];

  // Check if data spans multiple years
  const years = [...new Set(data.map((item) => item.year).filter(Boolean))];
  const hasMultipleYears = years.length > 1;

  return data.map((item) => ({
    month: hasMultipleYears && item.year ? `${item.month_name} ${item.year}` : item.month_name,
    closed: item.closed_prs,
    open: item.open_prs,
  }));
};

const SourcingSection = () => {
  const dispatch = useAppDispatch();
  const [dashboardTab, setDashboardTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [teamLeaderboardSortBy, setTeamLeaderboardSortBy] =
    useState<string>("total_pos");
  const {
    sourcingOverview,
    sourcingLoading,
    sourcingError,
    sourcingPerformance,
    sourcingPerformanceLoading,
    sourcingPerformanceError,
    teamLeaderboard,
    teamLeaderboardLoading,
    teamLeaderboardError,
    sourcingFilters,
  } = useSelector((state: RootState) => state.procurementDashboard);

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearSourcingError());

    // Fetch data only if not already loaded
    if (!sourcingOverview) {
      dispatch(fetchSourcingOverview(sourcingFilters));
    }
    if (!sourcingPerformance) {
      dispatch(fetchSourcingPerformance(sourcingFilters));
    }
    if (!teamLeaderboard) {
      dispatch(
        fetchTeamLeaderboard({
          ...sourcingFilters,
          sort_by: teamLeaderboardSortBy,
        }),
      );
    }
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchSourcingOverview(sourcingFilters));
    dispatch(fetchSourcingPerformance(sourcingFilters));
    dispatch(
      fetchTeamLeaderboard({
        ...sourcingFilters,
        sort_by: teamLeaderboardSortBy,
      }),
    );
  };

  const handleApplyFilters = (newFilters: FilterValues) => {
    dispatch(setSourcingFilters(newFilters));
    dispatch(fetchSourcingOverview(newFilters));
    dispatch(fetchSourcingPerformance(newFilters));
    dispatch(
      fetchTeamLeaderboard({ ...newFilters, sort_by: teamLeaderboardSortBy }),
    );
  };

  const handleTeamLeaderboardSortChange = (sortBy: string) => {
    setTeamLeaderboardSortBy(sortBy);
    dispatch(fetchTeamLeaderboard({ ...sourcingFilters, sort_by: sortBy }));
  };

  const activeFilterCount =
    (sourcingFilters.entity && sourcingFilters.entity.length > 0 ? 1 : 0) +
    (sourcingFilters.start_date || sourcingFilters.end_date ? 1 : 0);

  // Transform API data to component format
  const transformedOverviewData =
    sourcingOverview && sourcingPerformance
      ? {
          primaryCards: {
            totalPRs: {
              count: sourcingOverview.primarycards.total_prs,
              label: "Total PRs",
            },
            totalPOs: {
              count: sourcingOverview.primarycards.total_pos,
              label: "Total POs",
            },
            inProcessPRs: {
              count: sourcingOverview.primarycards.in_process_prs,
              label: "In-Process PRs",
            },
            nonCompliancePRs: {
              count: sourcingOverview.primarycards.non_compliance_prs,
              label: "Non-compliance PRs",
            },
            singleSourcePRs: {
              count: sourcingOverview.primarycards.single_source_prs,
              label: "Single Source PRs",
            },
          },
          secondaryCards: {
            openPRPct: sourcingOverview.secondarycards.open_pr_pct,
            closedPRPct: sourcingOverview.secondarycards.closed_pr_pct,
            issuedPOPct: sourcingOverview.secondarycards.issued_po_pct,
            closedPOPct: sourcingOverview.secondarycards.closed_po_pct,
            invoicedPOPct: sourcingOverview.secondarycards.invoiced_po_pct,
            receivedPOPct: sourcingOverview.secondarycards.received_po_pct,
          },
          valueCards: {
            pctAvgDays: sourcingOverview.valuecards.pct_avg_days,
            poValue: sourcingOverview.valuecards.po_value,
            totalSavingsAmount: sourcingOverview.valuecards.total_savings_amount,
          },
          prByMonth: transformMonthlyPRData(sourcingPerformance.pr_by_month),
          prByStrategy: sourcingOverview.pr_by_strategy.map((item) => ({
            strategy: item.sourcing_strategy,
            value: item.count,
          })),
          delayedVsOpenVsClosed: [],
        }
      : null;

  const transformedAnalyticsData = sourcingOverview
    ? {
        awardBySuppliers: sourcingOverview.award_by_suppliers_usd.map(
          (item) => ({
            supplier: item.supplier_name,
            value: item.total_award_usd,
            poCount: item.total_pos,
          }),
        ),
        awardByCategory: sourcingOverview.award_by_category_usd.map((item) => ({
          category: item.g42_category,
          value: item.percentage_share,
        })),
        awardByCommodity: sourcingOverview.award_by_commodity_usd.map((item) => ({
          commodity: item.commodity_name,
          value: item.total_award_usd,
        })),
        awardByEntityUSD: sourcingOverview.award_by_entity_usd.map((item) => ({
          entity: item.location_code,
          value: item.total_award_usd,
        })),
        awardByEntityPOCount: sourcingOverview.award_by_entity_po_count.map(
          (item) => ({
            entity: item.location_code,
            count: parseFormattedNumber(item.total_po_count),
          }),
        ),
        prByStatus: sourcingOverview.pr_by_status.map((item) => ({
          count: item.count,
          label: item.req_status
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          color:
            item.req_status === "partially_received"
              ? "#ffa94d"
              : item.req_status === "received"
                ? "#00d2aa"
                : "#5b6b9e",
        })),
      }
    : null;

  // Helper function to generate initials from buyer name
  const getInitials = (name: string): string => {
    const parts = name.split("_");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const transformedPerformanceData =
    sourcingPerformance && teamLeaderboard
      ? {
          teamLeaderboard: teamLeaderboard.team_leaderboard.map(
            (item, index) => ({
              rank: index + 1,
              name: item.buyer_name.replace(/_/g, " "),
              initials: getInitials(item.buyer_name),
              poCount: item.total_pos,
              poValue: item.total_po_value,
              savings: item.total_savings_amount_value,
            }),
          ),
          pctComparisonByBuyers:
            sourcingPerformance.pct_comparison_by_buyers.map((item) => ({
              buyer: item.buyer_name.replace(/_/g, " "),
              days: Math.round(item.avg_pct_days * 10) / 10,
            })),
          prByBuyers: sourcingPerformance.pr_by_buyers.map((item) => ({
            buyer: item.buyer_name.replace(/_/g, " "),
            completed: item.completed_pr_count,
            total: item.total_pr_count,
          })),
        }
      : null;

  // Mock data for Reports Dashboard (no API endpoint available)
  const mockReportsData = {
    activePRs: [
      {
        prId: "PR-2026-1245",
        specialist: {
          name: "Sarah Johnson",
          initials: "SJ",
          avatarColor: "#00d2aa",
        },
        status: "In Review" as const,
        assignedDate: "2026-02-28",
        estDate: "2026-03-10",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1244",
        specialist: {
          name: "Michael Chen",
          initials: "MC",
          avatarColor: "#5b6b9e",
        },
        status: "Approved" as const,
        assignedDate: "2026-02-25",
        estDate: "2026-03-08",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1243",
        specialist: {
          name: "Emma Williams",
          initials: "EW",
          avatarColor: "#00d2aa",
        },
        status: "Delayed" as const,
        assignedDate: "2026-02-20",
        estDate: "2026-02-28",
        delay: { type: "delayed" as const, days: 4 },
      },
      {
        prId: "PR-2026-1242",
        specialist: {
          name: "David Miller",
          initials: "DM",
          avatarColor: "#5b6b9e",
        },
        status: "Pending" as const,
        assignedDate: "2026-02-26",
        estDate: "2026-03-12",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1241",
        specialist: {
          name: "Jessica Brown",
          initials: "JB",
          avatarColor: "#00d2aa",
        },
        status: "In Review" as const,
        assignedDate: "2026-02-27",
        estDate: "2026-03-09",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1240",
        specialist: {
          name: "Sarah Johnson",
          initials: "SJ",
          avatarColor: "#00d2aa",
        },
        status: "Delayed" as const,
        assignedDate: "2026-02-15",
        estDate: "2026-02-25",
        delay: { type: "delayed" as const, days: 7 },
      },
    ],
    topSavings: {
      value: "$1.38M",
      subtitle: "15.2% total savings rate",
    },
    bestPCT: {
      value: "12 Days",
      subtitle: "Sarah Johnson's average",
    },
    completionRate: {
      value: "77.4%",
      subtitle: "902 of 1166 PRs closed",
    },
  };

  const renderDashboardContent = () => {
    const isLoading =
      sourcingLoading || sourcingPerformanceLoading || teamLeaderboardLoading;
    const hasError =
      sourcingError || sourcingPerformanceError || teamLeaderboardError;

    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {sourcingError || sourcingPerformanceError || teamLeaderboardError}
        </Alert>
      );
    }

    switch (dashboardTab) {
      case 0:
        return <OverviewDashboard {...(transformedOverviewData || {})} filters={sourcingFilters} />;
      case 1:
        return <AnalyticsDashboard {...(transformedAnalyticsData || {})} />;
      case 2:
        return (
          <PerformanceDashboard
            {...(transformedPerformanceData || {})}
            teamLeaderboardSortBy={teamLeaderboardSortBy}
            onTeamLeaderboardSortChange={handleTeamLeaderboardSortChange}
            filters={sourcingFilters}
          />
        );
      case 3:
        return <ReportsDashboard {...mockReportsData} />;
      default:
        return null;
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h4" className={styles.title}>
            Sourcing Analytics
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Purchase requisitions and procurement performance
          </Typography>
        </Box>
        <Box className={styles.headerActions}>
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              className={styles.actionButton}
              onClick={() => setFilterModalOpen(true)}
            >
              Filters
            </Button>
          </Badge>
          {/* <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            className={styles.actionButton}
          >
            Export
          </Button> */}
          <Button
            variant="outlined"
            startIcon={<ChatBubbleOutlineIcon />}
            className={styles.actionButton}
            onClick={() => setModalOpen(true)}
          >
            Talk with Data
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={
              sourcingLoading ||
              sourcingPerformanceLoading ||
              teamLeaderboardLoading
            }
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box className={styles.dashboardTabs}>
        <Tabs
          value={dashboardTab}
          onChange={(_e, val) => setDashboardTab(val)}
          className={styles.tabs}
          TabIndicatorProps={{
            style: { backgroundColor: "#00d2aa" },
          }}
        >
          <Tab
            icon={
              <img
                src={overviewIcon}
                alt="Overview Dashboard"
                style={{
                  width: 18,
                  height: 18,
                  marginRight: 6,
                  filter:
                    dashboardTab === 0
                      ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                      : "grayscale(100%) opacity(1)",
                }}
              />
            }
            label="Overview Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
          <Tab
            icon={
              <img
                src={analyticsIcon}
                alt="Analytics Dashboard"
                style={{
                  width: 18,
                  height: 18,
                  marginRight: 6,
                  filter:
                    dashboardTab === 1
                      ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                      : "grayscale(100%) opacity(1)",
                }}
              />
            }
            label="Analytics Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
          <Tab
            icon={
              <img
                src={performanceIcon}
                alt="Performance Dashboard"
                style={{
                  width: 18,
                  height: 18,
                  marginRight: 6,
                  filter:
                    dashboardTab === 2
                      ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                      : "grayscale(100%) opacity(1)",
                }}
              />
            }
            label="Performance Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
          {/* <Tab
            icon={
              <img
                src={supplierIcon}
                alt="Reports Dashboard"
                style={{
                  width: 18,
                  height: 18,
                  marginRight: 6,
                  filter:
                    dashboardTab === 3
                      ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                      : "grayscale(100%) opacity(1)",
                }}
              />
            }
            label="Reports Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          /> */}
        </Tabs>
      </Box>

      {renderDashboardContent()}
      <TalkWithDataModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        section="sourcing"
        initialFilters={sourcingFilters}
      />
    </Box>
  );
};

export default SourcingSection;
